/**
 * Reports this iframe's current location up to the embedding Salesforce parent
 * so the Salesforce-side "Pop Out" button can open the EXACT view the agent is
 * looking at, bound to the same client.
 *
 * The Salesforce toolbar and this app are served from different origins, so the
 * parent window cannot read this iframe's URL directly (cross-origin). We post
 * it up on every navigation instead. The page owns its own context (it carries
 * `sfId`/`sfObjectType` in the URL), so the parent just mirrors what it reports.
 *
 * @shared Consumed by the Salesforce Aura bundle:
 *   - salesforce/force-app/main/default/aura/Tsupport/TsupportHelper.js
 *     (registerIframeLocationListener / popOutIframe)
 *   The message shape MUST stay in sync with that listener:
 *     { type: 'tsupportLocation', href: <string> }
 */

const MESSAGE_TYPE = 'tsupportLocation'

// Only the ThriveSupport-in-Salesforce flows are ever popped out. Restrict
// reporting to those routes so other embedded surfaces stay silent.
const SUPPORTED_PATH = /^\/app\/(support|providers)(\/|$)/

// Guards against double-wrapping the global history methods if the bridge is
// ever started more than once before its cleanup runs.
let isBridgeActive = false

function isEmbedded(): boolean {
  try {
    return window.self !== window.top
  } catch {
    // A cross-origin parent throws on access — which means we are embedded.
    return true
  }
}

/**
 * Begin reporting location to the parent. Idempotent and safe to call on every
 * mount; returns a cleanup function. No-op on the server, when not embedded, or
 * outside the ThriveSupport routes.
 */
export function startSalesforceIframeLocationBridge(): () => void {
  if (typeof window === 'undefined' || !isEmbedded() || isBridgeActive) {
    return () => undefined
  }

  isBridgeActive = true

  const post = (): void => {
    if (!SUPPORTED_PATH.test(window.location.pathname)) {
      return
    }

    window.parent.postMessage(
      JSON.stringify({ type: MESSAGE_TYPE, href: window.location.href }),
      '*'
    )
  }

  // Report immediately — covers full page loads (iframe src changes, redirects).
  post()

  // Catch client-side (SPA) navigations in both the App Router and Pages Router
  // by wrapping the history methods and listening for popstate.
  const originalPushState = window.history.pushState
  const originalReplaceState = window.history.replaceState

  window.history.pushState = (
    ...args: Parameters<History['pushState']>
  ): void => {
    originalPushState.apply(window.history, args)
    post()
  }

  window.history.replaceState = (
    ...args: Parameters<History['replaceState']>
  ): void => {
    originalReplaceState.apply(window.history, args)
    post()
  }

  window.addEventListener('popstate', post)

  return () => {
    window.history.pushState = originalPushState
    window.history.replaceState = originalReplaceState
    window.removeEventListener('popstate', post)
    isBridgeActive = false
  }
}
